// Hex Map Component - Main map display using Konva.js
import Konva from 'konva';
import { CONFIG } from '../config.js';
import { tileRenderer } from './tile-renderer.js';
import { HexMath } from '../utils/hex-math.js';
import { iconLibrary } from '../utils/icon-library.js';
import { getCurrentRevealState, getCurrentTokenPosition, addTokenPositionHistoryEntry } from '../main.js';
import { HexEvents, createHexEvent } from '../utils/hex-events.js';
import { EventManagerMixin } from '../utils/event-manager.js';
import { getTokenInitials } from '../utils/ui-helpers.js';
import { addEvent, applyEvent, EventType } from '../utils/event-sourcing.js';

class HexMap extends EventManagerMixin(HTMLElement) {
    static get observedAttributes() {
        return ['view-mode'];
    }

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });

        // Map state
        this.session = null;
        this.mapData = null;
        this.mapDimensions = null;
        this.displayIds = {}; // Map of sourceId -> displayId

        // View mode: 'gm' (default) or 'player'
        this.viewMode = 'gm';

        // Konva objects
        this.stage = null;
        this.tileLayer = null;
        this.labelLayer = null;
        this.overlayLayer = null;
        this.iconLayer = null;
        this.tokenLayer = null;
        this.hoverLayer = null;

        // Hover state
        this.hoveredHex = null;

        // Selection state
        this.selectedHex = null;

        // Pulse effect state (Player View)
        this.pulsedHex = null;
        this.pulseAnimation = null;

        // Default view state (for animated reset in Player View)
        this.defaultViewState = null;
        this.viewResetTween = null;

        // Display IDs visibility state (default: hidden)
        this.showDisplayIds = false;

        // Zoom constraints (minScale is calculated dynamically based on map/canvas ratio)
        this.minScale = 0.1;  // Will be updated after map loads
        this.maxScale = 4;

        // BroadcastChannel for cross-window sync
        this.syncChannel = null;

        // Track dragging to distinguish from clicks
        this.isDragging = false;

        // Token placement mode (click-to-place)
        this.placingMode = false;
        this.placingTokenId = null;

        // ResizeObserver for cleanup
        this.resizeObserver = null;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'view-mode' && newValue) {
            this.viewMode = newValue;
        }
    }

    connectedCallback() {
        this.render();
    }

    disconnectedCallback() {
        // Clean up ResizeObserver
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        // Clean up Konva stage
        if (this.stage) {
            this.stage.destroy();
            this.stage = null;
        }
        // Clean up BroadcastChannel
        if (this.syncChannel) {
            this.syncChannel.close();
            this.syncChannel = null;
        }
    }

    render() {
        this.shadowRoot.innerHTML = `
            <style>
                :host {
                    display: block;
                    width: 100%;
                    height: 100%;
                    position: relative;
                    overflow: hidden;
                    background: #1a1a2e;
                }
                .map-container {
                    width: 100%;
                    height: 100%;
                    position: relative;
                }
                .loading, .waiting-overlay {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    color: #a0a0a0;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    z-index: ${CONFIG.Z_INDEX_BASE};
                }
            </style>
            <div class="map-container" id="container">
                <div class="loading" id="loading">Loading tiles...</div>
                <div class="waiting-overlay" id="waiting" style="display: none;">
                    <div style="text-align: center;">
                        <div style="font-size: 3rem; margin-bottom: 1rem;">🗺️</div>
                        <div style="font-size: 1.25rem; margin-bottom: 0.5rem;">Waiting for GM</div>
                        <div style="font-size: 0.875rem; opacity: 0.7;">No tiles revealed yet</div>
                    </div>
                </div>
            </div>
        `;
    }

    async loadMap(session, mapData) {
        this.session = session;
        this.mapData = mapData;

        // Get view mode from attribute
        this.viewMode = this.getAttribute('view-mode') || 'gm';

        const loading = this.shadowRoot.getElementById('loading');
        const container = this.shadowRoot.getElementById('container');

        try {
            // Get container dimensions
            const rect = container.getBoundingClientRect();
            const containerWidth = rect.width || 800;
            const containerHeight = rect.height || 600;

            // Destroy existing stage if any
            if (this.stage) {
                this.stage.destroy();
            }

            // Create Konva stage
            this.stage = new Konva.Stage({
                container: container,
                width: containerWidth,
                height: containerHeight,
                draggable: true
            });

            // Create layers
            this.tileLayer = new Konva.Layer();
            this.labelLayer = new Konva.Layer({ listening: false });
            this.iconLayer = new Konva.Layer({ listening: false });
            this.tokenLayer = new Konva.Layer({ listening: false });
            this.overlayLayer = new Konva.Layer({ listening: false });
            this.hoverLayer = new Konva.Layer({ listening: false });

            this.stage.add(this.tileLayer);
            this.stage.add(this.labelLayer);
            this.stage.add(this.iconLayer);
            this.stage.add(this.tokenLayer);
            this.stage.add(this.overlayLayer);
            this.stage.add(this.hoverLayer);

            // Render tiles to the tile layer with fog of war options
            const dimensions = await tileRenderer.renderToLayer(
                this.tileLayer,
                mapData,
                (stage, current, total) => {
                    if (stage === 'preloading') {
                        loading.textContent = `Loading tiles: ${current}/${total}`;
                    } else {
                        loading.textContent = `Rendering: ${current}/${total}`;
                    }
                },
                { session, viewMode: this.viewMode }
            );

            this.mapDimensions = dimensions;

            // Hide loading
            loading.style.display = 'none';

            // Setup zoom with wheel
            this.setupWheelZoom();

            // Setup click handling for GM view
            if (this.viewMode === 'gm') {
                this.setupClickHandling();
                this.setupDropHandling();
                this.setupKeyboardNavigation();
            }

            // Setup hover handling for both views
            this.setupHoverHandling();

            // Setup BroadcastChannel for cross-window sync
            this.setupSyncChannel();

            // Setup resize handler for responsive canvas
            this.setupResizeHandler();

            // Calculate display IDs from source coordinates
            const displayIdResult = HexMath.calculateDisplayIds(mapData.tiles);
            this.displayIds = displayIdResult.displayIds;

            // Render display IDs on tiles (GM view only, if enabled)
            // Note: showDisplayIds defaults to false, so IDs are hidden initially
            if (this.viewMode === 'gm' && this.showDisplayIds) {
                this.renderAllDisplayIds();
            }

            // Render icons (filtered by fog of war in player view)
            this.renderAllIcons();

            // Render tokens on map
            this.renderAllTokens();

            // Calculate zoom constraints and center the map
            this.calculateMinScale();
            this.resetView();

        } catch (error) {
            console.error('Error loading map:', error);
            loading.textContent = 'Failed to load map';
        }
    }

    /**
     * Calculate the minimum scale that fits the map exactly in the canvas
     * This becomes the zoom-out limit so the map always fills the viewport
     */
    calculateMinScale() {
        if (!this.mapDimensions || !this.stage) return;

        const container = this.shadowRoot.getElementById('container');
        if (!container) return;

        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        let targetWidth, targetHeight;

        if (this.viewMode === 'player') {
            const revealedBounds = this.getRevealedBounds();
            if (!revealedBounds) {
                this.minScale = 0.1;
                return;
            }
            targetWidth = revealedBounds.width;
            targetHeight = revealedBounds.height;
        } else {
            targetWidth = this.mapDimensions.width;
            targetHeight = this.mapDimensions.height;
        }

        // Calculate scale needed to fit map in canvas (the smaller ratio wins)
        const scaleX = containerWidth / targetWidth;
        const scaleY = containerHeight / targetHeight;

        // The minimum scale is where the map exactly fits the canvas
        this.minScale = Math.min(scaleX, scaleY);
    }

    setupWheelZoom() {
        this.stage.on('wheel', (e) => {
            e.evt.preventDefault();

            const oldScale = this.stage.scaleX();
            const pointer = this.stage.getPointerPosition();

            // Calculate position relative to stage
            const mousePointTo = {
                x: (pointer.x - this.stage.x()) / oldScale,
                y: (pointer.y - this.stage.y()) / oldScale
            };

            // Determine zoom direction
            const direction = e.evt.deltaY > 0 ? -1 : 1;
            const scaleBy = 1.1;
            let newScale = direction > 0 ? oldScale * scaleBy : oldScale / scaleBy;

            // Clamp scale
            newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

            this.stage.scale({ x: newScale, y: newScale });

            // Adjust position to zoom toward mouse
            const newPos = {
                x: pointer.x - mousePointTo.x * newScale,
                y: pointer.y - mousePointTo.y * newScale
            };

            this.stage.position(newPos);
        });
    }

    setupResizeHandler() {
        // Use ResizeObserver for smooth, immediate resize response
        this.resizeObserver = new ResizeObserver((entries) => {
            if (!this.stage) return;

            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                if (width > 0 && height > 0) {
                    // Update stage size immediately
                    this.stage.width(width);
                    this.stage.height(height);

                    // Recalculate zoom constraints for new container size
                    this.calculateMinScale();

                    // Re-center the map
                    this.resetView();
                }
            }
        });

        const container = this.shadowRoot.getElementById('container');
        if (container) {
            this.resizeObserver.observe(container);
        }
    }

    zoom(factor) {
        const oldScale = this.stage.scaleX();
        let newScale = oldScale * factor;

        // Clamp scale
        newScale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

        if (newScale === oldScale) return;

        // Zoom toward center of viewport
        const container = this.shadowRoot.getElementById('container');
        const centerX = container.clientWidth / 2;
        const centerY = container.clientHeight / 2;

        const mousePointTo = {
            x: (centerX - this.stage.x()) / oldScale,
            y: (centerY - this.stage.y()) / oldScale
        };

        this.stage.scale({ x: newScale, y: newScale });

        const newPos = {
            x: centerX - mousePointTo.x * newScale,
            y: centerY - mousePointTo.y * newScale
        };

        this.stage.position(newPos);
    }

    /**
     * Setup click handling for hex selection (GM view only)
     */
    setupClickHandling() {
        // Track drag state to distinguish from clicks
        this.stage.on('dragstart', () => {
            this.isDragging = true;
        });

        this.stage.on('dragend', () => {
            // Reset after a short delay so click doesn't fire
            setTimeout(() => {
                this.isDragging = false;
            }, 50);
        });

        // Right-click to toggle fog of war
        this.stage.on('contextmenu', (e) => {
            e.evt.preventDefault();

            const pointer = this.stage.getPointerPosition();
            const hexInfo = this.getHexAtPointer(pointer);
            if (!hexInfo || hexInfo.hexState === undefined) return;

            this.toggleFogForSelectedHex(hexInfo.coordKey);
        });

        this.stage.on('click tap', (e) => {
            // Ignore if we just finished dragging
            if (this.isDragging) return;

            // Get the clicked hex
            const pointer = this.stage.getPointerPosition();
            const hexInfo = this.getHexAtPointer(pointer);

            if (hexInfo && hexInfo.hexState !== undefined) {
                const { hexCoord, coordKey, hexState } = hexInfo;
                // Check if in placing mode (click-to-place)
                if (this.placingMode && this.placingTokenId) {
                    this.placeToken(this.placingTokenId, hexCoord.col, hexCoord.row);

                    // Exit placing mode and dispatch deselect event
                    this.exitPlacingMode();
                    this.dispatchEvent(createHexEvent(HexEvents.TOKEN_DESELECTED));

                    return; // Don't select the hex
                }

                // Draw selection highlight
                this.drawSelectionHighlight(hexCoord.col, hexCoord.row);

                // Dispatch event for left-sidebar to handle
                this.dispatchEvent(createHexEvent(HexEvents.HEX_CLICKED, {
                    x: hexCoord.col,
                    y: hexCoord.row,
                    coord: coordKey,
                    displayId: this.displayIds[coordKey] || null,
                    state: hexState
                }));

                // Broadcast hex selection to Player View (GM only)
                if (this.viewMode === 'gm' && this.syncChannel) {
                    this.syncChannel.postMessage({
                        type: 'hex-selected',
                        hexData: {
                            x: hexCoord.col,
                            y: hexCoord.row,
                            coord: coordKey,
                            displayId: this.displayIds[coordKey] || null,
                            state: hexState
                        }
                    });
                }
            } else {
                // Clear selection highlight
                this.clearSelectionHighlight();

                // Clicked on empty area - dispatch event to close panel
                this.dispatchEvent(createHexEvent(HexEvents.MAP_CLICKED));

                // Broadcast deselection to Player View (GM only)
                if (this.viewMode === 'gm' && this.syncChannel) {
                    this.syncChannel.postMessage({
                        type: 'hex-deselected'
                    });
                }
            }
        });
    }

    /**
     * Setup hover handling for hex highlight effect
     */
    setupHoverHandling() {
        // Track mouse movement to detect hex under cursor
        this.stage.on('mousemove', () => {
            const pointer = this.stage.getPointerPosition();
            const hexInfo = this.getHexAtPointer(pointer);

            if (!hexInfo) {
                this.clearHoverHighlight();
                return;
            }

            const { hexCoord, coordKey, hexState } = hexInfo;

            // In player view, only highlight revealed hexes
            if (this.viewMode === 'player' && hexState && !getCurrentRevealState(hexState)) {
                this.clearHoverHighlight();
                return;
            }

            if (hexState !== undefined) {
                // Only redraw if we moved to a different hex
                if (this.hoveredHex !== coordKey) {
                    this.hoveredHex = coordKey;
                    this.drawHoverHighlight(hexCoord.col, hexCoord.row);
                }
            } else {
                this.clearHoverHighlight();
            }
        });

        // Clear highlight when mouse leaves the stage
        this.stage.on('mouseleave', () => {
            this.clearHoverHighlight();
        });
    }

    /**
     * Setup drop handling for token placement (GM view only)
     */
    setupDropHandling() {
        // Use the Konva container div instead of individual canvases
        // (Konva creates multiple canvases, one per layer, so we need to listen on the container)
        const container = this.shadowRoot.getElementById('container');
        if (!container) return;

        // Prevent default dragover to allow drop
        this.addManagedListener(container, 'dragover', (e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
        });

        // Handle drop
        this.addManagedListener(container, 'drop', (e) => {
            e.preventDefault();

            // Get the token ID from the drag data
            const tokenId = e.dataTransfer.getData('text/plain');
            if (!tokenId) return;

            // Get drop position relative to container
            const rect = container.getBoundingClientRect();
            const containerX = e.clientX - rect.left;
            const containerY = e.clientY - rect.top;

            // Convert to pointer format and get hex
            const hexInfo = this.getHexAtPointer({ x: containerX, y: containerY });
            if (hexInfo && hexInfo.hexState !== undefined) {
                const { hexCoord } = hexInfo;
                // Update token position in session
                this.placeToken(tokenId, hexCoord.col, hexCoord.row);
            }
        });

        // Listen for token selection (click-to-place mode)
        this.addManagedListener(window, HexEvents.TOKEN_SELECTED, (e) => {
            this.enterPlacingMode(e.detail.tokenId);
        });

        // Listen for token deselection (exit placing mode)
        this.addManagedListener(window, HexEvents.TOKEN_DESELECTED, () => {
            this.exitPlacingMode();
        });
    }

    /**
     * Enter placing mode for click-to-place token placement
     * @param {string} tokenId - Token ID to place
     */
    enterPlacingMode(tokenId) {
        this.placingMode = true;
        this.placingTokenId = tokenId;

        // Change cursor on the container
        const container = this.shadowRoot.getElementById('container');
        if (container) {
            container.style.cursor = 'crosshair';
        }
    }

    /**
     * Exit placing mode
     */
    exitPlacingMode() {
        this.placingMode = false;
        this.placingTokenId = null;

        // Restore default cursor
        const container = this.shadowRoot.getElementById('container');
        if (container) {
            container.style.cursor = '';
        }
    }

    /**
     * Setup keyboard navigation for hex selection (GM view only)
     * Enables arrow keys to move selection between hexes
     */
    setupKeyboardNavigation() {
        if (!this.stage) return;

        // Make stage container focusable with custom focus indicator
        const container = this.stage.container();
        container.tabIndex = 1;

        // Add focus styles
        container.style.outline = 'none'; // Remove default outline
        container.style.transition = 'box-shadow 0.2s ease';

        // Add focus event listeners for visual indicator
        container.addEventListener('focus', () => {
            container.style.boxShadow = 'inset 0 0 0 3px rgba(59, 130, 246, 0.5)'; // Blue glow
        });

        container.addEventListener('blur', () => {
            container.style.boxShadow = 'none';
        });

        // Handle keyboard events
        const handleKeyDown = (e) => {
            // Only handle arrow keys, Enter/Space, and 'r' for reveal toggle
            if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Enter', ' ', 'r', 'R'].includes(e.key)) {
                return;
            }

            // If no hex is selected and arrow key pressed, select hex 0101 (top-left)
            if (!this.selectedHex && e.key.startsWith('Arrow')) {
                // Find hex with display ID 0101
                let hex0101Coord = null;
                for (const [coord, displayId] of Object.entries(this.displayIds)) {
                    if (displayId === '0101') {
                        hex0101Coord = coord;
                        break;
                    }
                }

                if (hex0101Coord && this.session?.hexStates?.[hex0101Coord]) {
                    // Select hex 0101
                    const [col, row] = hex0101Coord.split(',').map(Number);
                    this.drawSelectionHighlight(col, row);

                    // Dispatch event for left-sidebar to handle
                    const hexState = this.session.hexStates[hex0101Coord];
                    this.dispatchEvent(createHexEvent(HexEvents.HEX_CLICKED, {
                        x: col,
                        y: row,
                        coord: hex0101Coord,
                        displayId: '0101',
                        state: hexState
                    }));

                    // Broadcast hex selection to Player View
                    if (this.syncChannel) {
                        this.syncChannel.postMessage({
                            type: 'hex-selected',
                            hexData: {
                                x: col,
                                y: row,
                                coord: hex0101Coord,
                                displayId: '0101',
                                state: hexState
                            }
                        });
                    }

                    e.preventDefault();
                }
                return; // Don't process arrow navigation yet, wait for next arrow press
            }

            // Get current selection coordinates
            if (this.selectedHex && e.key.startsWith('Arrow')) {
                const [colStr, rowStr] = this.selectedHex.split(',');
                const col = parseInt(colStr, 10);
                const row = parseInt(rowStr, 10);

                // Determine direction
                let direction;
                switch (e.key) {
                    case 'ArrowUp':
                        direction = 'up';
                        break;
                    case 'ArrowDown':
                        direction = 'down';
                        break;
                    case 'ArrowLeft':
                        direction = 'left';
                        break;
                    case 'ArrowRight':
                        direction = 'right';
                        break;
                }

                // Get neighbor hex
                const neighbor = HexMath.getNeighbor(col, row, direction);
                const neighborKey = `${neighbor.col},${neighbor.row}`;

                // Check if neighbor hex exists in the map
                if (this.session && this.session.hexStates && this.session.hexStates[neighborKey]) {
                    // Move selection to neighbor
                    this.drawSelectionHighlight(neighbor.col, neighbor.row);

                    // Dispatch event for left-sidebar to handle
                    const hexState = this.session.hexStates[neighborKey];
                    this.dispatchEvent(createHexEvent(HexEvents.HEX_CLICKED, {
                        x: neighbor.col,
                        y: neighbor.row,
                        coord: neighborKey,
                        displayId: this.displayIds[neighborKey] || null,
                        state: hexState
                    }));

                    // Broadcast hex selection to Player View
                    if (this.syncChannel) {
                        this.syncChannel.postMessage({
                            type: 'hex-selected',
                            hexData: {
                                x: neighbor.col,
                                y: neighbor.row,
                                coord: neighborKey,
                                displayId: this.displayIds[neighborKey] || null,
                                state: hexState
                            }
                        });
                    }

                    e.preventDefault(); // Prevent page scroll
                }
            } else if (e.key === 'r' || e.key === 'R') {
                // 'R' key toggles reveal state of selected hex
                if (this.selectedHex) {
                    this.toggleFogForSelectedHex(this.selectedHex);
                    e.preventDefault();
                }
            } else if (e.key === 'Enter' || e.key === ' ') {
                // Enter/Space could toggle fog or perform some action
                // For now, we'll just prevent default
                e.preventDefault();
            }
        };

        // Add event listener
        this.addManagedListener(container, 'keydown', handleKeyDown);

        // Auto-focus container when hex is clicked (to enable immediate keyboard nav)
        this.addManagedListener(this, HexEvents.HEX_CLICKED, () => {
            container.focus();
        });
    }

    /**
     * Place a token on a hex
     * @param {string} tokenId - Token ID
     * @param {number} col - Hex column
     * @param {number} row - Hex row
     */
    placeToken(tokenId, col, row) {
        if (!window.currentSession || !window.currentSession.tokens) return;

        // Find the token
        const token = window.currentSession.tokens.find(t => t.id === tokenId);
        if (!token) return;

        // Update token position history
        addTokenPositionHistoryEntry(token, { col, row });

        // Dispatch event to notify sidebar
        this.dispatchEvent(createHexEvent(HexEvents.TOKEN_PLACED, {
            tokenId, col, row
        }));

        // Re-render all tokens
        this.renderAllTokens();
    }

    /**
     * Render all tokens on the map
     */
    renderAllTokens() {
        if (!this.tokenLayer || !this.session?.tokens) return;

        // Clear existing tokens
        this.tokenLayer.destroyChildren();

        // Get time travel day if active
        const timeTravelDay = window.currentSession?.timeTravelDay;

        // Filter tokens based on view mode
        let tokensToRender = this.session.tokens;
        if (this.viewMode === 'player') {
            // In player view, only show tokens with visibility 'show'
            tokensToRender = tokensToRender.filter(token => token.visibility === CONFIG.VISIBILITY_SHOW);
        }

        // Group tokens by position (using historical position if time traveling)
        const tokensByPosition = new Map();

        for (const token of tokensToRender) {
            const position = getCurrentTokenPosition(token, timeTravelDay);
            if (position) {
                const posKey = `${position.col},${position.row}`;
                if (!tokensByPosition.has(posKey)) {
                    tokensByPosition.set(posKey, []);
                }
                tokensByPosition.get(posKey).push(token);
            }
        }

        // Render each position
        for (const [posKey, tokens] of tokensByPosition) {
            if (tokens.length === 1) {
                // Single token - render normally
                this.renderToken(tokens[0], timeTravelDay);
            } else {
                // Multiple tokens - render count badge
                this.renderTokenStack(tokens, timeTravelDay);
            }
        }

        this.tokenLayer.batchDraw();
    }

    /**
     * Render a single token on the map
     * @param {Object} token - Token object
     * @param {number|null} timeTravelDay - Optional time travel day for historical positions
     */
    renderToken(token, timeTravelDay = null) {
        const position = getCurrentTokenPosition(token, timeTravelDay);
        if (!this.tokenLayer || !this.mapDimensions || !position) return;

        const { col, row } = position;
        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX || 0;
        const offsetY = this.mapDimensions.offsetY || 0;

        // Convert hex coordinates to pixel position
        const pixelPos = HexMath.hexToPixel(col, row, hexSize, offsetX, offsetY);

        // Create token visual (circle with initials or image)
        const tokenRadius = hexSize * CONFIG.RENDERING.TOKEN_RADIUS_RATIO;

        const tokenGroup = new Konva.Group({
            x: pixelPos.x,
            y: pixelPos.y,
            name: `token-${token.id}`,
            draggable: this.viewMode === 'gm' // Only draggable in GM view
        });

        // Background circle
        const circle = new Konva.Circle({
            radius: tokenRadius,
            fill: token.color || CONFIG.COLORS.TOKEN_DEFAULT,
            stroke: CONFIG.COLORS.TOKEN_STROKE,
            strokeWidth: 2
        });

        tokenGroup.add(circle);

        // Add initials or image
        if (token.imageDataUrl) {
            // Load and add image
            const img = new Image();
            img.onload = () => {
                const konvaImage = new Konva.Image({
                    image: img,
                    x: -tokenRadius,
                    y: -tokenRadius,
                    width: tokenRadius * 2,
                    height: tokenRadius * 2,
                    cornerRadius: tokenRadius
                });
                tokenGroup.add(konvaImage);
                this.tokenLayer.batchDraw();
            };
            img.src = token.imageDataUrl;
        } else {
            // Add initials text
            const initials = getTokenInitials(token.name);

            const text = new Konva.Text({
                text: initials,
                fontSize: tokenRadius * 0.8,
                fontFamily: 'Arial, sans-serif',
                fontStyle: 'bold',
                fill: CONFIG.COLORS.TOKEN_TEXT,
                align: 'center',
                verticalAlign: 'middle'
            });

            // Center the text
            text.offsetX(text.width() / 2);
            text.offsetY(text.height() / 2);

            tokenGroup.add(text);
        }

        // Add drag event handlers (only in GM view)
        if (this.viewMode === 'gm') {
            tokenGroup.on('dragstart', (e) => {
                // Prevent stage dragging while dragging token
                this.stage.draggable(false);

                // Change cursor
                tokenGroup.getStage().container().style.cursor = 'grabbing';
            });

            tokenGroup.on('dragend', (e) => {
                // Re-enable stage dragging
                this.stage.draggable(true);

                // Reset cursor
                tokenGroup.getStage().container().style.cursor = 'grab';

                // Get the token's new pixel position
                const newX = tokenGroup.x();
                const newY = tokenGroup.y();

                // Convert pixel position back to hex coordinates
                const hexCoord = HexMath.pixelToHex(
                    newX,
                    newY,
                    this.mapDimensions.hexSize,
                    this.mapDimensions.offsetX || 0,
                    this.mapDimensions.offsetY || 0
                );

                // Snap token to the hex center
                const snappedPixelPos = HexMath.hexToPixel(
                    hexCoord.col,
                    hexCoord.row,
                    this.mapDimensions.hexSize,
                    this.mapDimensions.offsetX || 0,
                    this.mapDimensions.offsetY || 0
                );

                tokenGroup.position({
                    x: snappedPixelPos.x,
                    y: snappedPixelPos.y
                });

                // Update token position history
                addTokenPositionHistoryEntry(token, { col: hexCoord.col, row: hexCoord.row });

                // Dispatch event to notify that token was moved
                this.dispatchEvent(createHexEvent(HexEvents.TOKEN_MOVED, {
                    tokenId: token.id,
                    col: hexCoord.col,
                    row: hexCoord.row,
                    token: token
                }));

                this.tokenLayer.batchDraw();
            });

            // Change cursor on hover
            tokenGroup.on('mouseenter', () => {
                tokenGroup.getStage().container().style.cursor = 'grab';
            });

            tokenGroup.on('mouseleave', () => {
                tokenGroup.getStage().container().style.cursor = 'default';
            });
        }

        this.tokenLayer.add(tokenGroup);
    }

    /**
     * Render a stack of tokens (2+) as a count badge
     * @param {Array} tokens - Array of token objects at the same position
     * @param {number|null} timeTravelDay - Optional time travel day for historical positions
     */
    renderTokenStack(tokens, timeTravelDay = null) {
        if (!this.tokenLayer || !this.mapDimensions || tokens.length === 0) return;

        const position = getCurrentTokenPosition(tokens[0], timeTravelDay);
        if (!position) return;
        const { col, row } = position;
        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX || 0;
        const offsetY = this.mapDimensions.offsetY || 0;

        // Convert hex coordinates to pixel position
        const pixelPos = HexMath.hexToPixel(col, row, hexSize, offsetX, offsetY);

        // Create stack badge visual
        const badgeRadius = hexSize * CONFIG.RENDERING.STACK_BADGE_RADIUS_RATIO;

        const stackGroup = new Konva.Group({
            x: pixelPos.x,
            y: pixelPos.y,
            name: `token-stack-${col}-${row}`
        });

        // Background circle with distinct color
        const circle = new Konva.Circle({
            radius: badgeRadius,
            fill: CONFIG.COLORS.DISPLAY_ID_BG,
            stroke: CONFIG.COLORS.SELECTION_STROKE,
            strokeWidth: 3
        });

        stackGroup.add(circle);

        // Count text
        const countText = new Konva.Text({
            text: tokens.length.toString(),
            fontSize: badgeRadius * 1.2,
            fontFamily: 'Arial, sans-serif',
            fontStyle: 'bold',
            fill: CONFIG.COLORS.DISPLAY_ID_TEXT,
            align: 'center',
            verticalAlign: 'middle'
        });

        // Center the text
        countText.offsetX(countText.width() / 2);
        countText.offsetY(countText.height() / 2);

        stackGroup.add(countText);

        // Make the stack clickable to select the hex
        stackGroup.on('click tap', () => {
            const coordKey = `${col},${row}`;
            // Dispatch hex-clicked event to select this hex
            this.dispatchEvent(createHexEvent(HexEvents.HEX_CLICKED, {
                x: col,
                y: row,
                displayId: this.displayIds[coordKey] || null,
                coord: coordKey,
                state: this.session?.hexStates?.[coordKey]
            }));
        });

        // Hover effect
        stackGroup.on('mouseenter', () => {
            circle.stroke(CONFIG.COLORS.SELECTION_HOVER);
            circle.strokeWidth(4);
            stackGroup.getStage().container().style.cursor = 'pointer';
            this.tokenLayer.batchDraw();
        });

        stackGroup.on('mouseleave', () => {
            circle.stroke(CONFIG.COLORS.SELECTION_STROKE);
            circle.strokeWidth(3);
            stackGroup.getStage().container().style.cursor = 'default';
            this.tokenLayer.batchDraw();
        });

        this.tokenLayer.add(stackGroup);
    }

    /**
     * Draw hex hover highlight with 1px border
     * @param {number} col - Hex column
     * @param {number} row - Hex row
     */
    drawHoverHighlight(col, row) {
        if (!this.hoverLayer || !this.mapDimensions) return;

        // Clear previous highlight
        this.hoverLayer.destroyChildren();

        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX || 0;
        const offsetY = this.mapDimensions.offsetY || 0;

        // Get hex center position
        const hexCenter = HexMath.hexToPixel(col, row, hexSize, offsetX, offsetY);

        // Get hex corners for the outline
        const corners = HexMath.getHexCorners(hexCenter.x, hexCenter.y, hexSize);

        // Convert corners to flat array of points for Konva.Line
        const points = corners.flatMap(c => [c.x, c.y]);

        // Draw hex outline
        const hexOutline = new Konva.Line({
            points: points,
            stroke: CONFIG.COLORS.HOVER_STROKE,
            strokeWidth: 1,
            closed: true,
            listening: false
        });

        this.hoverLayer.add(hexOutline);
        this.hoverLayer.batchDraw();
    }

    /**
     * Clear the hover highlight
     */
    clearHoverHighlight() {
        if (this.hoveredHex !== null) {
            this.hoveredHex = null;
            if (this.hoverLayer) {
                this.hoverLayer.destroyChildren();
                this.hoverLayer.batchDraw();
            }
        }
    }

    /**
     * Draw selection highlight on a hex (yellow border)
     * @param {number} col - Hex column
     * @param {number} row - Hex row
     */
    drawSelectionHighlight(col, row) {
        if (!this.overlayLayer || !this.mapDimensions) return;

        // Clear any existing selection highlight
        this.clearSelectionHighlight();

        this.selectedHex = `${col},${row}`;

        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX || 0;
        const offsetY = this.mapDimensions.offsetY || 0;

        const hexCenter = HexMath.hexToPixel(col, row, hexSize, offsetX, offsetY);
        const corners = HexMath.getHexCorners(hexCenter.x, hexCenter.y, hexSize);

        // Convert corners to flat array of points for Konva.Line
        const points = corners.flatMap(c => [c.x, c.y]);

        // Draw yellow hex outline for selection
        const selectionOutline = new Konva.Line({
            points: points,
            stroke: CONFIG.COLORS.HIGHLIGHT_YELLOW,
            strokeWidth: 3,
            closed: true,
            listening: false,
            name: 'selection-highlight'
        });

        this.overlayLayer.add(selectionOutline);
        this.overlayLayer.batchDraw();
    }

    /**
     * Clear the selection highlight
     */
    clearSelectionHighlight() {
        this.selectedHex = null;
        if (this.overlayLayer) {
            const selection = this.overlayLayer.findOne('.selection-highlight');
            if (selection) {
                selection.destroy();
                this.overlayLayer.batchDraw();
            }
        }
    }

    /**
     * Draw pulse effect on a hex (Player View only)
     * Creates an animated pulsing outline to draw attention to GM's selected hex
     */
    drawPulseEffect(col, row) {
        if (!this.overlayLayer || !this.mapDimensions) return;

        // Clear any existing pulse effect
        this.clearPulseEffect();

        this.pulsedHex = `${col},${row}`;

        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX || 0;
        const offsetY = this.mapDimensions.offsetY || 0;

        const hexCenter = HexMath.hexToPixel(col, row, hexSize, offsetX, offsetY);
        const corners = HexMath.getHexCorners(hexCenter.x, hexCenter.y, hexSize);

        // Convert corners to flat array of points for Konva.Line
        const points = corners.flatMap(c => [c.x, c.y]);

        // Create pulsing hex outline
        const pulseOutline = new Konva.Line({
            points: points,
            stroke: CONFIG.COLORS.HIGHLIGHT_BRIGHT_YELLOW,
            strokeWidth: 4,
            closed: true,
            name: 'pulse-effect',
            opacity: 0.8
        });

        this.overlayLayer.add(pulseOutline);

        // Create animation for the pulse effect
        this.pulseAnimation = new Konva.Animation((frame) => {
            // Pulse period: 1.5 seconds
            const period = 1500;
            const t = (frame.time % period) / period;

            // Use sine wave for smooth pulsing
            const pulse = Math.sin(t * Math.PI * 2) * 0.5 + 0.5;

            // Animate opacity (0.5 to 1.0)
            pulseOutline.opacity(0.5 + pulse * 0.5);

            // Animate stroke width (3 to 6)
            pulseOutline.strokeWidth(3 + pulse * 3);
        }, this.overlayLayer);

        this.pulseAnimation.start();
    }

    /**
     * Clear the pulse effect
     */
    clearPulseEffect() {
        this.pulsedHex = null;

        // Stop animation if running
        if (this.pulseAnimation) {
            this.pulseAnimation.stop();
            this.pulseAnimation = null;
        }

        // Remove the pulse outline
        if (this.overlayLayer) {
            const pulse = this.overlayLayer.findOne('.pulse-effect');
            if (pulse) {
                pulse.destroy();
                this.overlayLayer.batchDraw();
            }
        }
    }

    /**
     * Setup BroadcastChannel for cross-window fog sync
     */
    setupSyncChannel() {
        try {
            this.syncChannel = new BroadcastChannel(CONFIG.BROADCAST_CHANNEL_NAME);

            this.syncChannel.onmessage = async (event) => {
                const { type } = event.data;

                if (type === 'event') {
                    // New event sourcing system - apply event to session
                    const { event: sessionEvent } = event.data;

                    if (window.currentSession?.history !== undefined) {
                        // Check if event already exists (avoid duplicates)
                        const exists = window.currentSession.history.find(e => e.id === sessionEvent.id);
                        if (!exists) {
                            // Add event to history
                            window.currentSession.history.push(sessionEvent);
                            window.currentSession.history.sort((a, b) => a.id - b.id);

                            // Update currentEventId if this event is newer (or if currentEventId is undefined)
                            if (!window.currentSession.currentEventId || sessionEvent.id > window.currentSession.currentEventId) {
                                window.currentSession.currentEventId = sessionEvent.id;
                            }

                            // Apply the event to state
                            applyEvent(window.currentSession, sessionEvent);

                            // Update component's session reference
                            this.session = window.currentSession;

                            // Refresh visuals based on event type
                            if (sessionEvent.type === 'REVEAL_HEX' || sessionEvent.type === 'HIDE_HEX') {
                                await this.updateTileVisibility(sessionEvent.coord, sessionEvent.type === 'REVEAL_HEX');
                            } else if (sessionEvent.type === 'REVEAL_ALL' || sessionEvent.type === 'HIDE_ALL') {
                                await this.refreshFogOfWar();
                            }
                        }
                    } else {
                        console.warn(`[${this.viewMode}] No history in session, cannot process event`);
                    }
                } else if (type === 'request-state') {
                    // GM responds to player's request for current state
                    if (this.viewMode === 'gm' && this.session?.hexStates) {
                        this.syncChannel.postMessage({
                            type: 'state-sync',
                            hexStates: this.session.hexStates
                        });
                    }
                } else if (type === 'state-sync') {
                    // Player receives initial state from GM
                    if (this.viewMode === 'player') {
                        const { hexStates } = event.data;
                        // Apply received state - collect revealed coords first
                        const revealedCoords = [];
                        Object.entries(hexStates).forEach(([coord, state]) => {
                            if (this.session?.hexStates?.[coord]) {
                                // Copy full state (handles both old and new format)
                                this.session.hexStates[coord] = { ...state };
                            }
                            if (getCurrentRevealState(state)) {
                                revealedCoords.push(coord);
                            }
                        });
                        // Wait for all tiles to be added before redrawing
                        // Use Promise.all to properly await all async addTileToLayer calls
                        Promise.all(revealedCoords.map(coord => this.addTileToLayer(coord)))
                            .then(() => {
                                this.tileLayer.batchDraw();
                                this.resetView();
                            });
                    }
                } else if (type === 'display-ids-update') {
                    // Sync display IDs visibility from GM to player view
                    const { showDisplayIds } = event.data;
                    this.showDisplayIds = showDisplayIds;
                    this.updateDisplayIdsVisibility();
                } else if (type === 'hex-selected') {
                    // GM selected a hex - update Player View sidebar and show pulse effect
                    if (this.viewMode === 'player') {
                        const { hexData } = event.data;

                        // Animate view back to default (if not already there)
                        this.animateToDefaultView();

                        // Show pulse effect on the selected hex
                        this.drawPulseEffect(hexData.x, hexData.y);

                        // Dispatch event to update sidebar
                        this.dispatchEvent(createHexEvent(HexEvents.HEX_CLICKED, hexData));
                    }
                } else if (type === 'hex-deselected') {
                    // GM deselected hex - clear Player View sidebar and pulse effect
                    if (this.viewMode === 'player') {
                        // Clear pulse effect
                        this.clearPulseEffect();

                        this.dispatchEvent(createHexEvent(HexEvents.MAP_CLICKED));
                    }
                }
            };

            // Player view: request current state from GM
            if (this.viewMode === 'player') {
                this.syncChannel.postMessage({ type: 'request-state' });
            }
        } catch (e) {
            // BroadcastChannel not supported (e.g., older browsers)
            console.warn('BroadcastChannel not available for fog sync');
        }
    }

    /**
     * Toggle fog of war for a hex (shared logic for right-click and keyboard shortcut)
     * @param {string} hexCoord - Hex coordinate string (e.g., "3,5")
     */
    toggleFogForSelectedHex(hexCoord) {
        // Block editing in historical mode
        if (window.currentSession?.isReadOnly) {
            document.dispatchEvent(createHexEvent(HexEvents.SHOW_TOAST, {
                message: 'Cannot edit while viewing history'
            }));
            return;
        }

        const hexState = this.session?.hexStates?.[hexCoord];
        if (!hexState) return;

        const currentRevealed = getCurrentRevealState(hexState);
        const newRevealed = !currentRevealed;
        this.setFogState(hexCoord, newRevealed);
    }

    /**
     * Update fog of war for a single hex
     * @param {string} hexCoord - Hex coordinate string (e.g., "3,5")
     * @param {boolean} revealed - Whether the hex is revealed
     * @param {boolean} broadcast - Whether to broadcast to other windows
     */
    setFogState(hexCoord, revealed, broadcast = true) {
        // Require event sourcing system
        if (!window.currentSession?.history) {
            console.error('Event sourcing not initialized');
            return;
        }

        // Add event to history
        const event = addEvent(window.currentSession, {
            type: revealed ? EventType.REVEAL_HEX : EventType.HIDE_HEX,
            coord: hexCoord
        });

        // Update component's session reference to see the change
        this.session = window.currentSession;

        // Broadcast event to other windows
        if (broadcast && this.syncChannel) {
            this.syncChannel.postMessage({
                type: 'event',
                event: event
            });
        }

        // Update visual (event sourcing already updated state)
        this.updateTileVisibility(hexCoord, revealed);

        // Dispatch event to notify session state changed
        this.dispatchEvent(createHexEvent(HexEvents.HEX_STATE_CHANGED, {
            hexCoord,
            revealed
        }));
    }

    /**
     * Reveal or hide all hexes
     * @param {boolean} revealed - Whether to reveal or hide all
     */
    setAllFogState(revealed) {
        if (!this.session?.hexStates) return;

        // Require event sourcing system
        if (!window.currentSession?.history) {
            console.error('Event sourcing not initialized');
            return;
        }

        const hexCoords = Object.keys(this.session.hexStates);

        // Add single REVEAL_ALL or HIDE_ALL event
        const event = addEvent(window.currentSession, {
            type: revealed ? EventType.REVEAL_ALL : EventType.HIDE_ALL
        });

        // Update component's session reference
        this.session = window.currentSession;

        // Broadcast event to other windows
        if (this.syncChannel) {
            this.syncChannel.postMessage({
                type: 'event',
                event: event
            });
        }

        // Update visuals
        if (this.viewMode === 'gm') {
            // GM view: batch update all opacities at once
            hexCoords.forEach(coord => {
                const tileName = this.getTileName(coord);
                const tile = this.tileLayer.findOne(node => node.name() === tileName);
                if (tile) {
                    tile.opacity(revealed ? CONFIG.FOG_REVEALED_OPACITY : CONFIG.FOG_UNREVEALED_OPACITY);
                }
            });
            this.tileLayer.batchDraw();
        } else {
            // Player view: update tile visibility
            hexCoords.forEach(coord => {
                this.updateTileVisibility(coord, revealed);
            });
        }

        // Dispatch event to notify session state changed
        this.dispatchEvent(createHexEvent(HexEvents.HEX_STATE_CHANGED, {
            hexCoords,
            revealed,
            bulk: true
        }));
    }

    /**
     * Convert pointer/mouse position to hex coordinate
     * @param {Object} pointer - Pointer position from Konva (with x, y properties)
     * @returns {Object|null} - { hexCoord: {col, row}, coordKey: string, hexState: object } or null if invalid
     */
    getHexAtPointer(pointer) {
        if (!pointer) return null;

        const scale = this.stage.scaleX();
        const stagePos = this.stage.position();
        const mapX = (pointer.x - stagePos.x) / scale;
        const mapY = (pointer.y - stagePos.y) / scale;

        const hexSize = this.mapDimensions?.hexSize;
        const offsetX = this.mapDimensions?.offsetX || 0;
        const offsetY = this.mapDimensions?.offsetY || 0;

        if (!hexSize) return null;

        const hexCoord = HexMath.pixelToHex(mapX, mapY, hexSize, offsetX, offsetY);
        const coordKey = `${hexCoord.col},${hexCoord.row}`;
        const hexState = this.session?.hexStates?.[coordKey];

        return { hexCoord, coordKey, hexState };
    }

    /**
     * Get the Konva name for a tile (handles negative coords)
     */
    getTileName(hexCoord) {
        return HexMath.formatHexNameForSelector(hexCoord);
    }

    /**
     * Refresh fog of war for all hexes (used for time travel)
     */
    async refreshFogOfWar() {
        if (!this.session?.hexStates) {
            console.log('[refreshFogOfWar] No session or hexStates');
            return;
        }

        console.log('[refreshFogOfWar] Called, hexStates count:', Object.keys(this.session.hexStates).length);

        if (this.viewMode === 'gm') {
            // GM view: update opacity for all tiles
            let updateCount = 0;
            Object.keys(this.session.hexStates).forEach(coord => {
                const hexState = this.session.hexStates[coord];
                const revealed = getCurrentRevealState(hexState);
                const tileName = this.getTileName(coord);
                const tile = this.tileLayer.findOne(node => node.name() === tileName);
                if (tile) {
                    const newOpacity = revealed ? CONFIG.FOG_REVEALED_OPACITY : CONFIG.FOG_UNREVEALED_OPACITY;
                    tile.opacity(newOpacity);
                    updateCount++;
                }
            });
            console.log('[refreshFogOfWar] Updated', updateCount, 'tiles');
            this.tileLayer.batchDraw();
        } else {
            // Player view: create missing tiles and update visibility for all
            const tilesToCreate = [];

            for (const coord of Object.keys(this.session.hexStates)) {
                const hexState = this.session.hexStates[coord];
                const revealed = getCurrentRevealState(hexState);
                const tileName = this.getTileName(coord);
                let tile = this.tileLayer.findOne(node => node.name() === tileName);

                if (!tile && revealed) {
                    // Tile doesn't exist yet but should be revealed - queue for creation
                    tilesToCreate.push(coord);
                } else if (tile) {
                    // Tile exists - update visibility
                    tile.visible(revealed);
                }
            }

            // Create missing tiles
            if (tilesToCreate.length > 0) {
                await Promise.all(tilesToCreate.map(coord => this.addTileToLayer(coord)));
            }

            this.tileLayer.batchDraw();
        }
    }

    /**
     * Update tile visibility based on fog state
     * @param {string} hexCoord - Hex coordinate
     * @param {boolean} revealed - Whether revealed
     */
    async updateTileVisibility(hexCoord, revealed) {
        const tileName = this.getTileName(hexCoord);

        if (this.viewMode === 'gm') {
            // GM view: update opacity
            const tile = this.tileLayer.findOne(node => node.name() === tileName);
            if (tile) {
                tile.opacity(revealed ? CONFIG.FOG_REVEALED_OPACITY : CONFIG.FOG_UNREVEALED_OPACITY);
                this.tileLayer.batchDraw();
            }
        } else {
            // Player view: create tile if revealing and doesn't exist yet
            let tile = this.tileLayer.findOne(node => node.name() === tileName);

            if (!tile && revealed) {
                // Tile doesn't exist yet - create it
                try {
                    await this.addTileToLayer(hexCoord);
                    tile = this.tileLayer.findOne(node => node.name() === tileName);
                } catch (error) {
                    console.error(`[Player View] Error creating tile ${hexCoord}:`, error);
                }
            }

            if (tile) {
                tile.visible(revealed);
                this.tileLayer.batchDraw();
                console.log(`[Player View] Tile visibility set to ${revealed}, layer children count=${this.tileLayer.children.length}`);
            } else {
                console.warn(`[Player View] No tile to update for ${hexCoord}`);
            }
            // Recalculate view to fit revealed tiles
            console.log(`[Player View] Calling resetView()`);
            this.resetView();
        }
    }

    /**
     * Add a tile to the layer (for player view when revealing)
     */
    async addTileToLayer(hexCoord) {
        const [x, y] = hexCoord.split(',').map(Number);

        // Find the tile data
        const tileData = this.mapData?.tiles?.find(t => t.pos.x === x && t.pos.y === y);
        if (!tileData) return;

        // Ensure map dimensions are available before adding tiles
        if (!this.mapDimensions?.hexSize) {
            console.warn(`Cannot add tile ${hexCoord}: mapDimensions not yet set`);
            return;
        }

        try {
            const img = await tileRenderer.loadTile(tileData.tileId);
            const hexSize = this.mapDimensions.hexSize;
            const offsetX = this.mapDimensions.offsetX || 0;
            const offsetY = this.mapDimensions.offsetY || 0;

            const pos = HexMath.hexToPixel(x, y, hexSize, offsetX, offsetY);
            // Use tile.rot if defined, otherwise use CONFIG base rotation
            const tileRotation = tileData.rot !== undefined ? tileData.rot : 30;
            const konvaImage = tileRenderer.createTileImage(img, tileData.tileId, pos.x, pos.y, tileRotation);
            konvaImage.name(this.getTileName(hexCoord));

            this.tileLayer.add(konvaImage);
            console.log(`[addTileToLayer] Added tile ${hexCoord} to layer, visible=${konvaImage.visible()}, opacity=${konvaImage.opacity()}`);
            // Don't batchDraw here - let caller handle batching for better performance
        } catch (e) {
            console.warn(`Failed to add tile ${hexCoord}: ${e.message}`);
        }
    }

    /**
     * Calculate bounding box of revealed tiles only
     */
    getRevealedBounds() {
        if (!this.session?.hexStates || !this.mapDimensions) return null;

        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX;
        const offsetY = this.mapDimensions.offsetY;

        let minX = Infinity, minY = Infinity;
        let maxX = -Infinity, maxY = -Infinity;
        let hasRevealed = false;

        for (const [coord, state] of Object.entries(this.session.hexStates)) {
            if (getCurrentRevealState(state)) {
                hasRevealed = true;
                const [hx, hy] = coord.split(',').map(Number);
                const pos = HexMath.hexToPixel(hx, hy, hexSize, offsetX, offsetY);

                minX = Math.min(minX, pos.x - hexSize);
                minY = Math.min(minY, pos.y - hexSize);
                maxX = Math.max(maxX, pos.x + hexSize);
                maxY = Math.max(maxY, pos.y + hexSize);
            }
        }

        if (!hasRevealed) return null;

        return {
            minX, minY,
            width: maxX - minX,
            height: maxY - minY
        };
    }

    resetView() {
        if (!this.stage || !this.mapDimensions) return;

        const container = this.shadowRoot.getElementById('container');
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;

        let targetWidth, targetHeight, targetOffsetX = 0, targetOffsetY = 0;

        let scale;

        if (this.viewMode === 'player') {
            // Player view: fit revealed tiles only
            const revealedBounds = this.getRevealedBounds();
            console.log(`[resetView] revealedBounds:`, revealedBounds);
            if (!revealedBounds) {
                // No revealed tiles - show nothing or center
                this.stage.scale({ x: 1, y: 1 });
                this.stage.position({ x: 0, y: 0 });
                console.log(`[resetView] No revealed tiles, reset to 1:1`);
                return;
            }

            targetWidth = revealedBounds.width;
            targetHeight = revealedBounds.height;
            targetOffsetX = revealedBounds.minX;
            targetOffsetY = revealedBounds.minY;

            // Calculate scale to fit ONLY the revealed tiles
            const scaleX = containerWidth / targetWidth;
            const scaleY = containerHeight / targetHeight;
            scale = Math.min(scaleX, scaleY) * 0.9; // 0.9 for padding
            console.log(`[resetView] Calculated scale=${scale}, targetWidth=${targetWidth}, targetHeight=${targetHeight}`);
        } else {
            // GM view: fit entire map
            targetWidth = this.mapDimensions.width;
            targetHeight = this.mapDimensions.height;

            // Use the minScale which is already calculated to fit the map exactly
            scale = this.minScale;
        }

        // Center the target area
        const offsetX = (containerWidth - targetWidth * scale) / 2 - targetOffsetX * scale;
        const offsetY = (containerHeight - targetHeight * scale) / 2 - targetOffsetY * scale;

        this.stage.scale({ x: scale, y: scale });
        this.stage.position({ x: offsetX, y: offsetY });
        console.log(`[resetView] Stage positioned: scale=${scale}, position=(${offsetX}, ${offsetY})`);

        // Store default view state for Player View animated reset
        if (this.viewMode === 'player') {
            this.defaultViewState = {
                scale: scale,
                x: offsetX,
                y: offsetY
            };
        }
    }

    /**
     * Animate Player View back to default view state
     * Called when GM selects a hex to ensure player can see it
     */
    animateToDefaultView() {
        if (!this.stage || !this.defaultViewState || this.viewMode !== 'player') return;

        // Stop any existing animation
        if (this.viewResetTween) {
            this.viewResetTween.destroy();
            this.viewResetTween = null;
        }

        // Get current view state
        const currentScale = this.stage.scaleX();
        const currentPos = this.stage.position();

        // Check if we're already at default view (within small threshold)
        const scaleThreshold = 0.01;
        const posThreshold = 5;

        const atDefaultScale = Math.abs(currentScale - this.defaultViewState.scale) < scaleThreshold;
        const atDefaultX = Math.abs(currentPos.x - this.defaultViewState.x) < posThreshold;
        const atDefaultY = Math.abs(currentPos.y - this.defaultViewState.y) < posThreshold;

        // Skip animation if already at default view
        if (atDefaultScale && atDefaultX && atDefaultY) {
            return;
        }

        // Create smooth tween animation (3 seconds)
        this.viewResetTween = new Konva.Tween({
            node: this.stage,
            duration: 3,
            scaleX: this.defaultViewState.scale,
            scaleY: this.defaultViewState.scale,
            x: this.defaultViewState.x,
            y: this.defaultViewState.y,
            easing: Konva.Easings.EaseInOut,
            onFinish: () => {
                this.viewResetTween = null;
            }
        });

        this.viewResetTween.play();
    }

    /**
     * Render all display IDs onto the label layer
     */
    renderAllDisplayIds() {
        if (!this.labelLayer || !this.mapDimensions || !this.displayIds) return;

        // Clear existing labels
        this.labelLayer.destroyChildren();

        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX || 0;
        const offsetY = this.mapDimensions.offsetY || 0;

        // Calculate font size to fit within hex side width
        // Hex side length equals circumradius for flat-top hexagons
        const sideLength = hexSize;
        // 4 characters need to fit, with some padding
        const fontSize = Math.floor(sideLength * 0.28);

        // Render display ID for each hex
        for (const [sourceId, displayId] of Object.entries(this.displayIds)) {
            const [x, y] = sourceId.split(',').map(Number);
            const hexCenter = HexMath.hexToPixel(x, y, hexSize, offsetX, offsetY);

            // Position at top of hex (flat-top hex has flat edge at top)
            // The top flat edge is at y = center.y - (height/2) = center.y - (sqrt(3)/2 * hexSize)
            const hexHeight = hexSize * Math.sqrt(3);
            const topY = hexCenter.y - hexHeight / 2;

            // Create a group for the label (background + text for contrast)
            const labelGroup = new Konva.Group({
                x: hexCenter.x,
                y: topY + fontSize * CONFIG.RENDERING.DISPLAY_ID_VERTICAL_OFFSET,
                name: `label_${sourceId.replace(',', '_')}`
            });

            // Background rectangle for readability (sized for 4 monospace chars)
            const bgWidth = fontSize * 2.8;
            const bgHeight = fontSize * 1.3;
            const bg = new Konva.Rect({
                x: -bgWidth / 2,
                y: -bgHeight / 2,
                width: bgWidth,
                height: bgHeight,
                fill: CONFIG.COLORS.OVERLAY_DARK,
                cornerRadius: 2
            });
            labelGroup.add(bg);

            // Display ID text with monospace font
            const text = new Konva.Text({
                x: -bgWidth / 2,
                y: -fontSize / 2,
                width: bgWidth,
                text: displayId,
                fontSize: fontSize,
                fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                fontStyle: 'bold',
                fill: CONFIG.COLORS.DISPLAY_ID_TEXT,
                align: 'center'
            });
            labelGroup.add(text);

            this.labelLayer.add(labelGroup);
        }

        this.labelLayer.batchDraw();
    }

    /**
     * Toggle display IDs visibility on the map
     * @returns {boolean} The new visibility state
     */
    toggleDisplayIds() {
        this.showDisplayIds = !this.showDisplayIds;
        this.updateDisplayIdsVisibility();

        // Broadcast state change to player view
        if (this.syncChannel) {
            this.syncChannel.postMessage({
                type: 'display-ids-update',
                showDisplayIds: this.showDisplayIds
            });
        }

        return this.showDisplayIds;
    }

    /**
     * Set display IDs visibility state
     * @param {boolean} visible - Whether to show display IDs
     */
    setDisplayIdsVisible(visible) {
        this.showDisplayIds = visible;
        this.updateDisplayIdsVisibility();
    }

    /**
     * Update the label layer based on showDisplayIds state
     */
    updateDisplayIdsVisibility() {
        if (!this.labelLayer) return;

        if (this.showDisplayIds) {
            // Show display IDs
            this.renderAllDisplayIds();
        } else {
            // Hide display IDs
            this.labelLayer.destroyChildren();
            this.labelLayer.batchDraw();
        }
    }

    /**
     * Calculate days elapsed between two game dates
     * @param {Object} startDate - Starting date {year, month, day}
     * @param {Object} currentDate - Current date {year, month, day}
     * @returns {number} Days elapsed
     */
    calculateDaysElapsed(startDate, currentDate) {
        if (!startDate || !currentDate) return 0;

        const yearDiff = currentDate.year - startDate.year;
        const monthDiff = currentDate.month - startDate.month;
        const dayDiff = currentDate.day - startDate.day;

        return yearDiff * 360 + monthDiff * 30 + dayDiff;
    }

    /**
     * Render all icons from session state onto the icon layer
     */
    async renderAllIcons() {
        if (!this.iconLayer || !this.session?.hexStates) return;

        // Clear existing icons
        this.iconLayer.destroyChildren();

        // Check if we're in time travel mode
        const timeTravelDay = window.currentSession?.timeTravelDay;
        const startDate = this.session.calendar?.startDate;

        // Render icons for each hex that has them
        for (const [coord, state] of Object.entries(this.session.hexStates)) {
            // In player view, only render icons for revealed hexes
            if (this.viewMode === 'player' && !getCurrentRevealState(state)) {
                continue;
            }

            if (state.icons && state.icons.length > 0) {
                // Filter icons by date if in time travel mode
                let iconsToRender = state.icons;

                if (timeTravelDay && startDate) {
                    iconsToRender = state.icons.filter(icon => {
                        // Icons without gameDate are shown for backward compatibility
                        if (!icon.gameDate) return true;

                        const iconDay = this.calculateDaysElapsed(startDate, icon.gameDate);
                        return iconDay <= timeTravelDay;
                    });
                }

                if (iconsToRender.length > 0) {
                    await this.renderIconsForHex(coord, iconsToRender);
                }
            }
        }

        this.iconLayer.batchDraw();
    }

    /**
     * Render icons for a specific hex
     * @param {string} hexCoord - Hex coordinate string (e.g., "3,5")
     * @param {Array} icons - Array of icon objects
     */
    async renderIconsForHex(hexCoord, icons) {
        if (!this.iconLayer || !this.mapDimensions) return;

        const [x, y] = hexCoord.split(',').map(Number);
        const hexSize = this.mapDimensions.hexSize;
        const offsetX = this.mapDimensions.offsetX || 0;
        const offsetY = this.mapDimensions.offsetY || 0;

        // Get hex center position
        const hexCenter = HexMath.hexToPixel(x, y, hexSize, offsetX, offsetY);

        // Icon size relative to hex
        const iconSize = hexSize * 0.4;
        const maxIconsPerRow = 3;

        // Filter icons by visibility in Player View (only show 'show' icons, hide 'secret' icons)
        let iconsToRender = icons;
        if (this.viewMode === 'player') {
            iconsToRender = icons.filter(icon => icon.visibility === CONFIG.VISIBILITY_SHOW);
        }

        // Render each icon
        for (let index = 0; index < iconsToRender.length; index++) {
            const icon = iconsToRender[index];

            // Calculate position for this icon (grid layout within hex)
            const row = Math.floor(index / maxIconsPerRow);
            const col = index % maxIconsPerRow;
            const totalInRow = Math.min(iconsToRender.length - row * maxIconsPerRow, maxIconsPerRow);

            // Center the icons within the hex
            const startX = hexCenter.x - ((totalInRow - 1) * iconSize * 0.6);
            const iconX = startX + col * iconSize * 1.2;
            const iconY = hexCenter.y + row * iconSize * 1.2 - (iconsToRender.length > maxIconsPerRow ? iconSize * 0.3 : 0);

            // Create icon group
            const iconGroup = new Konva.Group({
                x: iconX,
                y: iconY,
                name: `icon_${icon.id}`
            });

            // Background circle for visibility
            const bgCircle = new Konva.Circle({
                x: 0,
                y: 0,
                radius: iconSize * 0.5,
                fill: CONFIG.COLORS.OVERLAY_DARKER,
                stroke: icon.visibility === CONFIG.VISIBILITY_SECRET ? CONFIG.COLORS.VISIBILITY_SECRET_COLOR : CONFIG.COLORS.VISIBILITY_SHOW_COLOR,
                strokeWidth: 2
            });
            iconGroup.add(bgCircle);

            // Load and render SVG icon
            try {
                const svgData = await iconLibrary.loadSvg(icon.iconName);

                // Parse viewBox to get dimensions
                const viewBoxParts = svgData.viewBox.split(' ').map(Number);
                const vbWidth = viewBoxParts[2] || 512;
                const vbHeight = viewBoxParts[3] || 512;

                // Calculate scale to fit icon within circle
                const targetSize = iconSize * 0.6; // Slightly smaller than circle
                const scale = targetSize / Math.max(vbWidth, vbHeight);

                // Create SVG path
                const svgPath = new Konva.Path({
                    x: -targetSize / 2,
                    y: -targetSize / 2,
                    data: svgData.path,
                    fill: icon.color,
                    scaleX: scale,
                    scaleY: scale
                });
                iconGroup.add(svgPath);
            } catch (error) {
                console.warn(`Failed to load icon ${icon.iconName}:`, error);
                // Fallback to circle
                const fallbackCircle = new Konva.Circle({
                    x: 0,
                    y: 0,
                    radius: iconSize * 0.2,
                    fill: icon.color
                });
                iconGroup.add(fallbackCircle);
            }

            this.iconLayer.add(iconGroup);
        }
    }

    /**
     * Get a simple symbol representation for common icons
     * Since Konva can't render Font Awesome directly, we use Unicode approximations
     * @param {string} iconName - Font Awesome icon name (e.g., "fa-dragon")
     * @returns {string} Unicode symbol or placeholder
     */
    getIconSymbol(iconName) {
        // Map common icons to Unicode symbols
        const iconMap = {
            // Creatures
            'fa-dragon': '🐉',
            'fa-spider': '🕷',
            'fa-skull': '💀',
            'fa-ghost': '👻',
            'fa-horse': '🐴',
            'fa-cat': '🐱',
            'fa-dog': '🐕',
            'fa-crow': '🐦',
            'fa-frog': '🐸',
            'fa-fish': '🐟',
            'fa-dove': '🕊',
            // People
            'fa-user': '👤',
            'fa-user-shield': '🛡',
            'fa-user-ninja': '🥷',
            'fa-user-secret': '🕵',
            'fa-hat-wizard': '🧙',
            'fa-mask': '🎭',
            'fa-person': '🧍',
            'fa-people-group': '👥',
            'fa-skull-crossbones': '☠',
            // Treasure
            'fa-gem': '💎',
            'fa-coins': '🪙',
            'fa-crown': '👑',
            'fa-trophy': '🏆',
            'fa-gift': '🎁',
            'fa-ring': '💍',
            'fa-diamond': '💠',
            'fa-medal': '🏅',
            // Locations
            'fa-castle': '🏰',
            'fa-tent': '⛺',
            'fa-monument': '🗿',
            'fa-campground': '🏕',
            'fa-mountain': '⛰',
            'fa-volcano': '🌋',
            'fa-house': '🏠',
            'fa-church': '⛪',
            'fa-landmark': '🏛',
            'fa-bridge': '🌉',
            'fa-city': '🏙',
            'fa-building': '🏢',
            'fa-dungeon': '🚪',
            // Hazards
            'fa-fire': '🔥',
            'fa-biohazard': '☣',
            'fa-radiation': '☢',
            'fa-triangle-exclamation': '⚠',
            'fa-bomb': '💣',
            'fa-bolt-lightning': '⚡',
            'fa-explosion': '💥',
            'fa-ban': '🚫',
            'fa-tornado': '🌪',
            // Quest
            'fa-flag': '🚩',
            'fa-location-dot': '📍',
            'fa-star': '⭐',
            'fa-question': '❓',
            'fa-check': '✅',
            'fa-bookmark': '🔖',
            'fa-bullseye': '🎯',
            'fa-crosshairs': '⊕',
            'fa-map-pin': '📌',
            'fa-route': '🛤',
            'fa-compass': '🧭',
            'fa-map': '🗺',
            'fa-scroll': '📜',
            'fa-key': '🔑'
        };

        return iconMap[iconName] || '●';
    }

    /**
     * Update icons for a specific hex (called when icons are added/removed)
     * Currently re-renders all icons for simplicity
     * @param {string} hexCoord - Hex coordinate string (unused, kept for API compatibility)
     */
    updateIconsForHex(hexCoord) {
        // Re-render all icons - more efficient selective update could be added later
        this.renderAllIcons();
    }

    /**
     * Refresh all icons on the map
     * Call this when icons are added, removed, or modified
     */
    refreshIcons() {
        this.renderAllIcons();
    }

    /**
     * Refresh all tokens on the map
     * Call this when tokens are moved or time travel state changes
     */
    refreshTokens() {
        this.renderAllTokens();
    }

    /**
     * Refresh the entire map to fix tile misalignment
     * Recalculates scale and redraws all layers
     */
    refreshMap() {
        // Recalculate and reset view to fix alignment
        this.calculateMinScale();
        this.resetView();

        // Refresh all layers
        if (this.tileLayer) this.tileLayer.batchDraw();
        if (this.fogLayer) this.fogLayer.batchDraw();
        if (this.iconLayer) this.iconLayer.batchDraw();
        if (this.tokenLayer) this.tokenLayer.batchDraw();
    }

    /**
     * Refresh map display for time travel or state changes
     * Re-renders fog, icons, and tokens to reflect historical state
     */
    async refreshMapDisplay() {
        // Re-render fog layer (respects historical state)
        if (this.fogLayer) {
            this.fogLayer.batchDraw();
        }

        // Re-render icons (filtered by historical state)
        if (this.renderIcons) {
            await this.renderIcons();
        }

        // Re-render tokens (filtered by historical state)
        this.renderAllTokens();
    }

    /**
     * Export the map for printing
     * @param {string} mode - 'gm' for all hexes, 'player' for revealed + outlines
     * @returns {Promise<string>} Data URL of the exported image
     */
    async exportForPrint(mode = 'gm') {
        if (!this.mapData || !this.mapDimensions) {
            throw new Error('Map not loaded');
        }

        // Use the actual dimensions from the loaded map (which already uses actual bounds)
        const { width, height, hexSize, offsetX, offsetY } = this.mapDimensions;

        // Create off-screen container
        const offscreenContainer = document.createElement('div');
        offscreenContainer.style.position = 'absolute';
        offscreenContainer.style.left = '-9999px';
        offscreenContainer.style.top = '-9999px';
        document.body.appendChild(offscreenContainer);

        // Create temporary Konva stage at full map dimensions
        const printStage = new Konva.Stage({
            container: offscreenContainer,
            width: width,
            height: height
        });

        const tileLayer = new Konva.Layer();
        const labelLayer = new Konva.Layer();
        printStage.add(tileLayer);
        printStage.add(labelLayer);

        // Sort tiles for proper draw order (top to bottom, left to right)
        const tiles = [...this.mapData.tiles].sort((a, b) => {
            if (a.pos.y !== b.pos.y) return a.pos.y - b.pos.y;
            return a.pos.x - b.pos.x;
        });

        // Render tiles based on mode
        for (const tile of tiles) {
            const hexCoord = `${tile.pos.x},${tile.pos.y}`;
            const hexState = this.session?.hexStates?.[hexCoord];
            const isRevealed = hexState?.revealed ?? false;

            const pos = HexMath.hexToPixel(tile.pos.x, tile.pos.y, hexSize, offsetX, offsetY);

            if (mode === 'gm') {
                // GM mode: render all tiles at full opacity
                try {
                    const img = await tileRenderer.loadTile(tile.tileId);
                    const tileRotation = tile.rot !== undefined ? tile.rot : 30;
                    const konvaImage = tileRenderer.createTileImage(img, tile.tileId, pos.x, pos.y, tileRotation);
                    konvaImage.opacity(1.0);
                    tileLayer.add(konvaImage);
                } catch (e) {
                    console.warn(`Skipping tile ${tile.tileId}: ${e.message}`);
                }
            } else {
                // Player mode: revealed tiles show imagery, unrevealed show outline
                if (isRevealed) {
                    try {
                        const img = await tileRenderer.loadTile(tile.tileId);
                        const tileRotation = tile.rot !== undefined ? tile.rot : 30;
                        const konvaImage = tileRenderer.createTileImage(img, tile.tileId, pos.x, pos.y, tileRotation);
                        tileLayer.add(konvaImage);
                    } catch (e) {
                        console.warn(`Skipping tile ${tile.tileId}: ${e.message}`);
                    }
                } else {
                    // Draw empty hex outline for unrevealed tiles
                    const corners = HexMath.getHexCorners(pos.x, pos.y, hexSize);
                    const points = corners.flatMap(c => [c.x, c.y]);
                    const hexOutline = new Konva.Line({
                        points: points,
                        stroke: CONFIG.COLORS.GRID_LINE,
                        strokeWidth: 1,
                        closed: true
                    });
                    tileLayer.add(hexOutline);
                }
            }
        }

        // Render display ID labels on ALL hexes
        const hexHeight = hexSize * Math.sqrt(3);
        const fontSize = Math.floor(hexSize * 0.28);
        const bgWidth = fontSize * 2.8;
        const bgHeight = fontSize * 1.3;

        for (const [sourceId, displayId] of Object.entries(this.displayIds)) {
            const [x, y] = sourceId.split(',').map(Number);
            const hexCenter = HexMath.hexToPixel(x, y, hexSize, offsetX, offsetY);
            const topY = hexCenter.y - hexHeight / 2;

            // Background rectangle
            const bg = new Konva.Rect({
                x: hexCenter.x - bgWidth / 2,
                y: topY + fontSize * 0.6 - bgHeight / 2,
                width: bgWidth,
                height: bgHeight,
                fill: CONFIG.COLORS.OVERLAY_DARK,
                cornerRadius: 2
            });
            labelLayer.add(bg);

            // Display ID text
            const text = new Konva.Text({
                x: hexCenter.x - bgWidth / 2,
                y: topY + fontSize * 0.6 - fontSize / 2,
                width: bgWidth,
                text: displayId,
                fontSize: fontSize,
                fontFamily: "'Consolas', 'Monaco', 'Courier New', monospace",
                fontStyle: 'bold',
                fill: CONFIG.COLORS.DISPLAY_ID_TEXT,
                align: 'center'
            });
            labelLayer.add(text);
        }

        tileLayer.batchDraw();
        labelLayer.batchDraw();

        // Export to data URL at 2x resolution for high-quality print
        const dataUrl = printStage.toDataURL({
            pixelRatio: 2,
            mimeType: 'image/png'
        });

        // Clean up
        printStage.destroy();
        document.body.removeChild(offscreenContainer);

        return dataUrl;
    }
}

customElements.define('hex-map', HexMap);

export default HexMap;

/**
 * Helper function to refresh map display from outside the component
 * @param {string} mapId - ID of the hex-map element (optional, defaults to first hex-map)
 */
export async function refreshMapDisplay(mapId) {
    const hexMap = mapId ? document.getElementById(mapId) : document.querySelector('hex-map');
    if (hexMap && hexMap.refreshMapDisplay) {
        await hexMap.refreshMapDisplay();
    }
}
